import { queryKeys } from "@/queryKeys";
import api from "@/services/api";
import type { AppStatus } from "@/types/borrower";

import type { ContextRef } from "../protocol/protocol";

/** A row of GET agent/threads/: the user's web threads, latest message first. */
export interface AgentThreadEntry {
  id: number;
  /** "chat" is the General thread; "application" threads are bound to their context. */
  profile: string;
  context_ref: ContextRef | null;
  title: string | null;
  url: string | null;
  model_ref: string | null;
  rev: number;
  last_message_at: string | null;
  created_at: string;
}

export const agentThreadsQueryOptions = {
  queryKey: queryKeys.agentThreads(),
  queryFn: async () =>
    (await api.get<{ threads: AgentThreadEntry[] }>("/agent/threads/")).data.threads,
  staleTime: 30_000,
};

/** GET borrowers/<id>/quick-look/ (apps/borrowers/quick_look.py). */
export interface QuickLook {
  id: number;
  company_name: string;
  app_status: AppStatus;
  analysis: {
    revenue_lines: { month: string; amount: number }[];
    average_revenue: number | null;
    leverage: { percent: number; target_percent: number; monthly_payout: number } | null;
  } | null;
  offer: {
    funding_amount: number;
    factor: number | null;
    term_days: number | null;
    payment: number | null;
    frequency: "daily" | "weekly" | "monthly" | null;
    payback: number | null;
    sent_at: string;
  } | null;
  requested_amount: number | null;
  /** The status's name and when the application entered it. */
  stage: { label: string; since: string } | null;
  /**
   * The application's latest activity: who (null: the system) and when. Note metadata, so absent
   * for anyone who isn't funder staff (the notes' own gate).
   */
  last_activity?: { who: string | null; at: string } | null;
  broker: { company_name: string; email: string } | null;
  default_history_count: number | null;
}

export const quickLookQueryOptions = (id: number) => ({
  queryKey: queryKeys.applicationQuickLook(id),
  queryFn: async () => (await api.get<QuickLook>(`/borrowers/${id}/quick-look/`)).data,
  staleTime: 30_000,
});

/** Unlinks the deal the General chat is working on. */
export const unlinkThreadContext = (threadId: number) =>
  api.delete(`/agent/threads/${threadId}/context/`);
